import * as React from 'react';

import {
  Paper,
  Button,
  Typography,
  Grid,
  Snackbar,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { useStyles } from './styles';
import UserData from 'components/CarsQuotation/UserData/UserData';
import SubTitle from 'components/CarsQuotation/SubTitle/SubTitle';
import QuoteType from 'components/CarsQuotation/QuoteType/QuoteType';
import VehicleInfo from 'components/CarsQuotation/VehicleInfo/VehicleInfo';
import {
  insures,
  civilStatus,
  quoteTypeData,
  documentType,
  stratum,
  educationLevel as educationLevelItems,
  occupation,
  profession,
  services,
  useForVehicleParticular,
  useForVehiclePublic,
  defaultValues,
} from './resources';
import { TEventTarget } from 'types';
import { formatDate } from 'utils/utils';
import {
  createQuotation,
  getFasecoldaCode,
  getValidateQuotation,
  getRefFasecolda,
  getValidateOpportunity,
} from 'services/QuotationService';
import { cities } from './cities';
import matchSorter from 'match-sorter';
import { ISelect } from 'types/CarsQuotation/IDataInsure';
import { LICENSE_PLATE_VALIDATION } from 'constants/index';
import swal from 'sweetalert';
import { BootstrapInput } from 'components/Global/PickerSelect/PickerSelect';
import { useInsurers } from 'hooks/useInsurers';

const CarsQuotation: React.FC = () => {
  const classes = useStyles();

  const location = useLocation() as ILocationCarsQuotation;

  const { defaultValues: userDefaultValues } = useInsurers();

  const { handleSubmit, control, errors, watch, setValue, reset } = useForm<ICarsQuotation>({
    defaultValues: userDefaultValues || defaultValues,
  });
  const watchAllFields = watch(['tVehicle', 'service', 'checkAccesories']);

  React.useEffect(() => {
    if (userDefaultValues && !location.state?.quotation) {
      reset(userDefaultValues);
    }
  }, [userDefaultValues, reset, location.state]);

  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [openSnack, setOpenSnack] = React.useState<boolean>(false);
  const [enableValueInsuredEdit, setEnableValueInsuredEdit] = React.useState<boolean>(false);
  const [refsFasecolda, setRefsFasecolda] = React.useState<any>({});

  React.useEffect(() => {
    const currentTVehicle = watchAllFields.tVehicle;
    const codeFasecolda = watch('codeFasecolda');
    const modelo = watch('modelo');

    if (currentTVehicle === 'usado' && codeFasecolda && modelo) {
      if (Object.keys(refsFasecolda).length > 0) {
        const fModelo = `f${modelo}`;
        const valueInsure = refsFasecolda[fModelo];
        if (valueInsure && !enableValueInsuredEdit) {
          setValue('valueInsured', valueInsure);
        }
      } else {
        // Si no tenemos los datos, cargarlos primero
        setFasecoldaRef(codeFasecolda).then((response) => {
          if (response && modelo) {
            const fModelo = `f${modelo}`;
            const valueInsure = response[fModelo];
            if (valueInsure && !enableValueInsuredEdit) {
              setValue('valueInsured', valueInsure);
            }
          }
        });
      }
    }
    // Resetear el estado de edición cuando cambia el tipo de vehículo
    if (currentTVehicle === 'nuevo') {
      setEnableValueInsuredEdit(false);
    }
  }, [watchAllFields.tVehicle, watch, setValue, refsFasecolda, enableValueInsuredEdit]);
  const [disabledFasecoldaField, setDisabledFasecoldaField] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [typeQuote, setTypeQuote] = React.useState('Auto');

  const handleChangeTypeQuote = (event: any) => {
    setTypeQuote(event.target.value);
  };

  const toggleValueInsuredEdit = () => {
    setEnableValueInsuredEdit(!enableValueInsuredEdit);
  };

  const onError = (error: string) => {
    setOpenSnack(true);
    setErrorMessage(error);
  };

  const history = useHistory();

  const filterCities = (inputValue: string) => {
    const filterCitiesBySearch =
      cities !== null
        ? matchSorter(cities, inputValue, {
            keys: ['value', 'label'],
          })
        : [];
    return filterCitiesBySearch;
  };

  const loadCitiesOptions = (inputValue: string, callback: any) => {
    setTimeout(() => {
      callback(filterCities(inputValue));
    }, 500);
  };

  const handleInputCitiesChange = (newValue: string) => {
    const inputValue = newValue;
    return inputValue;
  };

  const onCloseSnackBar = () => {
    setOpenSnack(false);
  };

  const onValidateLicensePlate = (event: TEventTarget, props: any) => {
    const validationRegex = LICENSE_PLATE_VALIDATION;
    const plate = event.target.value;

    if (validationRegex.test(plate)) {
      setLoading(true);
      getFasecoldaCode(plate, onError)
        .then(({ fasecolda, modelo }) => {
          if (watchAllFields.tVehicle === 'usado' && fasecolda && modelo) {
            const fModelo = `f${modelo}`;

            setValue('codeFasecolda', fasecolda);
            setValue('modelo', modelo);

            // Resetear el modo de edición cuando se carga un nuevo vehículo
            setEnableValueInsuredEdit(false);
            setFasecoldaRef(fasecolda).then((response) =>
              setValue('valueInsured', response[fModelo])
            );
            setDisabledFasecoldaField(true);
            setLoading(false);
          } else {
            swal(
              '¡Alto!',
              'Esta placa está matricula en el runt o esta siendo usada en otro vehiculo.',
              'warning'
            );
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
      return props.onBlur(plate);
    } else {
      onError('Ups, La placa ingresada no es valida.');
      setDisabledFasecoldaField(false);
      return props.onBlur(plate);
    }
  };

  const onCreateQuotation = (bodyObject: any) => {
    createQuotation(bodyObject, onError).then(({ uuid }) => {
      history.push({
        pathname: `/app/cotizador/autos/resumen/${uuid}`,
      });
      return uuid;
    });
  };

  const setFasecoldaRef = async (fasecoldaCode: string | undefined) => {
    if (fasecoldaCode !== undefined) {
      setLoading(true);
      try {
        const refs = await getRefFasecolda(fasecoldaCode, onError);
        setRefsFasecolda(refs.guiaFasecolda.data);
        setValue('brand', refs.guiaFasecolda.data.fMarca);
        setValue('ref1', refs.guiaFasecolda.data.fReferencia1);
        setValue('ref2', refs.guiaFasecolda.data.fReferencia2);
        setValue('ref3', refs.guiaFasecolda.data.fReferencia3);

        setLoading(false);
        return refs.guiaFasecolda.data;
      } catch (err) {
        setLoading(false);
      }
    }
  };

  const onChangeFasecolda = (event: TEventTarget, props: any) => {
    if (event.target.value !== '') {
      setDisabledFasecoldaField(false);
      setFasecoldaRef(event.target.value);
      return props.onBlur(event.target.value);
    }
    return props.onBlur(event.target.value);
  };

  const onModeloChange = (event: TEventTarget, props: any) => {
    if (event.target.value !== '') {
      setLoading(true);
      const fModelo = `f${event.target.value}`;
      const valueInsure = refsFasecolda[fModelo];
      // Solo actualizar el valor si el modo de edición está deshabilitado
      if (!enableValueInsuredEdit && valueInsure) {
        setValue('valueInsured', valueInsure);
      }
      setLoading(false);
      return props.onBlur(event.target.value);
    } else {
      setLoading(false);
      return props.onBlur(event.target.value);
    }
  };

  const onSubmitQuotationForm = (formDataState: ICarsQuotation) => {
    const carModelo = formDataState.modelo;
    const valueInsuredAsNumber = parseInt(formDataState.valueInsured, 10);

    if (valueInsuredAsNumber !== 0) {
      const newSelectedInsurers: string[] = [];

      formDataState.selectedInsurers.map((insure: ISelect) =>
        newSelectedInsurers.push(insure.value)
      );

      const bodyObject = {
        ...formDataState,
        quoteType: typeQuote,
        selectedInsurers: newSelectedInsurers,
        cityCirculation:
          formDataState.optionalCity.value === undefined
            ? [formDataState.cityCirculation.value]
            : [formDataState.cityCirculation.value, formDataState.optionalCity.value],
        nDocument: parseInt(formDataState.nDocument, 10),
        stratum: parseInt(formDataState.stratum, 10),
        modelo: carModelo !== undefined && parseInt(carModelo, 10),
        valueInsured: valueInsuredAsNumber,
        valueAccesories: formDataState.valueAccesories
          ? parseInt(formDataState.valueAccesories, 10)
          : 0,
        fBorn: formDataState.fBorn,
        nChildren: parseInt(formDataState.nChildren as any, 10) || 0,
      };

      handleValidationAndQuotation(formDataState, bodyObject);
    } else {
      swal(
        `Lo sentimos`,
        `El valor asegurado no puede ser 0, revisa el formulario si los datos ingresados son corrector e intenta de nuevo.`,
        'error'
      );
    }
  };

  const showMessage = (title: string, text: string, icon: 'info' | 'warning' | 'error') => {
    swal(title, text, icon);
  };

  const showOpportunityModal = (response: any, onContinue: () => void) => {
    swal({
      title: 'Ya existe una oportunidad vigente en Salesforce asociada a esta cotización.',
      content: {
        element: 'span',
        attributes: {
          innerHTML: `
            <div style="text-align: left;">
              <b>Fecha de cotización:</b> ${formatDate(response.createdAt)}<br>
              <b>Placa:</b> ${response.licenseVehicle}<br>
              <b>Modelo:</b> ${response.modelo}<br>
              <b>Asesor:</b> ${response.createdBy.name}<br>
              <a href="https://dev.cotizadorvirtual.co/app/cotizador/autos/resumen/${
                response.uuid
              }" target="_blank">
                Ver Cotización
              </a>
            </div>
            <style>
              .swal-button-container {
                display: flex;
                justify-content: center;
                gap: 10px;
                width: 100%;
                margin-top: 20px;
              }
              .swal-button {
                flex: 1;
              }
            </style>`,
        },
      },
      icon: 'error',
      buttons: {
        cancel: {
          text: 'Cancelar',
          value: null,
          visible: true,
          className: '',
          closeModal: true,
        },
        confirm: {
          text: 'Continuar',
          value: 'continue',
          visible: true,
          className: '',
          closeModal: true,
        },
      },
    } as any).then((value) => {
      if (value === 'continue') {
        confirmRecotization(onContinue);
      }
    });
  };

  const confirmRecotization = (onConfirm: () => void) => {
    swal({
      title: '¿Está seguro de querer continuar?',
      text: 'Al recotizar, se actualizarán los datos existentes en la oportunidad. ¿Está seguro de continuar?',
      icon: 'warning',
      buttons: ['Cancelar', 'Sí, continuar'],
      dangerMode: true,
    }).then((confirmValue) => {
      if (confirmValue) {
        onConfirm();
      } else {
        showMessage('Proceso cancelado', 'No se realizó la cotización.', 'info');
      }
    });
  };

  const handleValidationAndQuotation = async (formDataState: ICarsQuotation, bodyObject: any) => {
    try {
      let isApiError = false;

      // Validación de oportunidad en Salesforce (no bloqueante en caso de error de API)
      const opportunity = await getValidateOpportunity(
        formDataState.licenseVehicle,
        formDataState.nDocument,
        (salesforceError) => {
          if (salesforceError === 'api_error') {
            isApiError = true;
          }
        }
      );

      // Si hubo error de API (500), mostrar advertencia y esperar confirmación
      if (isApiError) {
        // Mostrar advertencia y esperar a que el usuario la cierre
        await swal({
          title: 'Advertencia de Salesforce',
          text: 'No se pudo conectar con Salesforce en este momento. La cotización se realizará pero la información no se sincronizará con Salesforce.',
          icon: 'warning',
          buttons: {
            confirm: {
              text: 'Entendido, continuar',
              value: true,
              visible: true,
            },
          },
        });
      } else if (!opportunity) {
        // Si no hay oportunidad (404), mostrar error y NO permitir continuar
        showMessage(
          'Error',
          'Debe existir una oportunidad válida en Salesforce, antes de cotizar este vehículo',
          'error'
        );
        return; // Bloquear la cotización
      }

      // Continuar con la cotización
      setLoading(true);
      const quotationResponse = await getValidateQuotation(formDataState.licenseVehicle, onError);

      if (quotationResponse.message) {
        onCreateQuotation(bodyObject);
      } else {
        showOpportunityModal(quotationResponse, () => onCreateQuotation(bodyObject));
      }
    } catch (error) {
      onError('Ha ocurrido un error al validar la cotización, por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    document.title = 'Cotizador de autos';
    if (location.state !== undefined) {
      const quoteByLocation = location.state.quotation;
      const quotationTransform = Object.entries(quoteByLocation);
      quotationTransform.map((quote) => setValue(quote[0], quote[1]));
    }
  }, [location, setValue]);

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit(onSubmitQuotationForm)}>
        <Paper className={classes.paper}>
          <Typography component="h1" variant="h4" align="center" className={classes.title}>
            Cotizador - Seguro Todo Riesgo Autos
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SubTitle title="Tipo de Cotización" />
            </Grid>
            <QuoteType
              control={control}
              quoteType={quoteTypeData}
              disabledInsures={false}
              disabledQuoteType
              setValue={setValue}
            />
            <Grid item xs={12} sm={4}>
              <FormControl className={classes.margin} disabled>
                <Typography className={classes.label}>Ramo a Cotizar</Typography>
                <Select
                  labelId="demo-customized-select-label"
                  id="demo-customized-select"
                  value={typeQuote}
                  onChange={handleChangeTypeQuote}
                  input={<BootstrapInput />}>
                  <MenuItem value={'Auto'}>Auto</MenuItem>
                  <MenuItem value={'moto'}>Moto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <SubTitle title="Datos Personales" />
            </Grid>
            <UserData
              control={control}
              civilStatusItems={civilStatus}
              documentTypeItems={documentType}
              stratumItems={stratum}
              educationLevelItems={educationLevelItems}
              occupationItems={occupation}
              professionItems={profession}
              errors={errors}
            />
            <Grid item xs={12}>
              <SubTitle title="Información del Vehículo" />
            </Grid>
            <VehicleInfo
              control={control}
              serviceItems={services}
              useVehicleItems={
                watchAllFields.service === 'particular'
                  ? useForVehicleParticular
                  : useForVehiclePublic
              }
              loadCitiesOptions={loadCitiesOptions}
              handleInputCitiesChange={handleInputCitiesChange}
              errors={errors}
              onValidateLicensePlate={onValidateLicensePlate}
              checkboxAccesories={watchAllFields.checkAccesories}
              onChangeFasecolda={onChangeFasecolda}
              disabledFasecoldaField={disabledFasecoldaField}
              onModeloChange={onModeloChange}
              disableSecuredValue={
                watchAllFields.tVehicle === 'nuevo' ? false : !enableValueInsuredEdit
              }
              enableValueInsuredEdit={enableValueInsuredEdit}
              onToggleValueInsuredEdit={toggleValueInsuredEdit}
            />
            <Grid item xs={12}>
              <div className={classes.buttons}>
                {loading ? (
                  <CircularProgress color="primary" thickness={4} />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    type="submit"
                    disableElevation>
                    Cotizar
                  </Button>
                )}
              </div>
              {Object.keys(errors).length > 0 && (
                <Typography
                  variant="caption"
                  display="block"
                  gutterBottom
                  align="center"
                  style={{ color: 'red' }}>
                  Hay campos incompletos o con errores, por favor revisarlos.
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
        <Snackbar open={openSnack} autoHideDuration={6000} onClose={onCloseSnackBar}>
          <MuiAlert elevation={6} variant="filled" onClose={onCloseSnackBar} severity="error">
            {errorMessage}
          </MuiAlert>
        </Snackbar>
      </form>
    </React.Fragment>
  );
};

export default CarsQuotation;
